import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiTag, FiPlus, FiEdit, FiTrash2, FiEye, FiCheck, FiX, FiPause, FiPlay,
  FiClock, FiArchive, FiRefreshCw, FiDollarSign, FiPercent, FiUsers,
  FiCalendar, FiTrendingUp, FiFilter, FiSearch, FiLoader, FiAlertCircle,
  FiInfo, FiBarChart2
} from 'react-icons/fi';
import { apiUrls } from '../config/api';

interface Coupon {
  id: number;
  code: string;
  name: string;
  description: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_purchase_amount: number | null;
  max_discount_amount: number | null;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'expired' | 'archived';
  start_date: string;
  end_date: string;
  scheduled_activation_date: string | null;
  max_total_uses: number | null;
  max_uses_per_user: number;
  current_uses: number;
  applicable_to: 'all' | 'new_users' | 'existing_users' | 'specific_users';
  applicable_content_types: string[] | null;
  created_at: string;
  updated_at: string;
}

interface CouponFormData {
  code: string;
  name: string;
  description: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: string;
  min_purchase_amount: string;
  max_discount_amount: string;
  start_date: string;
  end_date: string;
  max_total_uses: string;
  max_uses_per_user: string;
  applicable_to: 'all' | 'new_users' | 'existing_users' | 'specific_users';
  applicable_content_types: string[];
  notes: string;
}

const CouponManagement: React.FC = () => {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState<any>(null);
  
  const [formData, setFormData] = useState<CouponFormData>({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase_amount: '',
    max_discount_amount: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    max_total_uses: '',
    max_uses_per_user: '1',
    applicable_to: 'all',
    applicable_content_types: [],
    notes: ''
  });

  useEffect(() => {
    fetchCoupons();
    fetchStats();
  }, [statusFilter]);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${apiUrls.coupons.admin}?status=${statusFilter}&pageSize=100`);
      const data = await response.json();
      if (data.success) {
        setCoupons(data.coupons || []);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(apiUrls.coupons.statsOverview);
      const data = await response.json();
      if (data.success) {
        setStats(data.statistics);
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  const handleCreate = async () => {
    try {
      const payload = {
        ...formData,
        code: formData.code.toUpperCase().trim(),
        discount_value: parseFloat(formData.discount_value),
        min_purchase_amount: formData.min_purchase_amount ? parseFloat(formData.min_purchase_amount) : null,
        max_discount_amount: formData.max_discount_amount ? parseFloat(formData.max_discount_amount) : null,
        max_total_uses: formData.max_total_uses ? parseInt(formData.max_total_uses) : null,
        max_uses_per_user: parseInt(formData.max_uses_per_user),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        status: 'draft'
      };

      const response = await fetch(apiUrls.coupons.create, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        setShowCreateModal(false);
        fetchCoupons();
        fetchStats();
        resetForm();
      } else {
        alert(data.error || 'Failed to create coupon');
      }
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const handleLifecycleAction = async (couponId: number, action: string, notes?: string) => {
    try {
      const actionUrls: any = {
        activate: apiUrls.coupons.activate,
        pause: apiUrls.coupons.pause,
        resume: apiUrls.coupons.resume,
        expire: apiUrls.coupons.expire,
        archive: apiUrls.coupons.archive
      };
      
      const response = await fetch(actionUrls[action](couponId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: notes || '' })
      });

      const data = await response.json();
      if (data.success) {
        fetchCoupons();
        fetchStats();
        if (selectedCoupon && selectedCoupon.id === couponId) {
          setSelectedCoupon(data.coupon);
        }
      } else {
        alert(data.error || `Failed to ${action} coupon`);
      }
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const handleDelete = async (couponId: number) => {
    if (!window.confirm('Are you sure you want to delete this coupon? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(apiUrls.coupons.delete(couponId), {
        method: 'DELETE'
      });

      const data = await response.json();
      if (data.success) {
        fetchCoupons();
        fetchStats();
      } else {
        alert(data.error || 'Failed to delete coupon');
      }
    } catch (err: any) {
      alert('Error: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      name: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase_amount: '',
      max_discount_amount: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      max_total_uses: '',
      max_uses_per_user: '1',
      applicable_to: 'all',
      applicable_content_types: [],
      notes: ''
    });
  };

  const getStatusColor = (status: string) => {
    const colors: {[key: string]: string} = {
      draft: '#6b7280',
      scheduled: '#f59e0b',
      active: '#10b981',
      paused: '#8b5cf6',
      expired: '#ef4444',
      archived: '#374151'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status: string) => {
    const icons: {[key: string]: JSX.Element} = {
      draft: <FiEdit />,
      scheduled: <FiClock />,
      active: <FiCheck />,
      paused: <FiPause />,
      expired: <FiX />,
      archived: <FiArchive />
    };
    return icons[status] || <FiInfo />;
  };

  const filteredCoupons = coupons.filter(coupon =>
    coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    coupon.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container>
      <Header>
        <Title><FiTag /> Coupon Management</Title>
        <ActionButton onClick={() => setShowCreateModal(true)}>
          <FiPlus /> Create Coupon
        </ActionButton>
      </Header>

      {/* Statistics Dashboard */}
      {stats && (
        <StatsGrid>
          <StatCard>
            <StatLabel>Total Coupons</StatLabel>
            <StatValue>{stats.total_coupons}</StatValue>
          </StatCard>
          <StatCard color="#10b981">
            <StatLabel>Active</StatLabel>
            <StatValue>{stats.active_coupons}</StatValue>
          </StatCard>
          <StatCard color="#f59e0b">
            <StatLabel>Draft</StatLabel>
            <StatValue>{stats.draft_coupons}</StatValue>
          </StatCard>
          <StatCard color="#ef4444">
            <StatLabel>Expired</StatLabel>
            <StatValue>{stats.expired_coupons}</StatValue>
          </StatCard>
          <StatCard color="#8b5cf6">
            <StatLabel>Total Usage</StatLabel>
            <StatValue>{stats.total_usages}</StatValue>
          </StatCard>
          <StatCard color="#06b6d4">
            <StatLabel>Discount Given</StatLabel>
            <StatValue>${stats.total_discount_given?.toFixed(2) || '0.00'}</StatValue>
          </StatCard>
        </StatsGrid>
      )}

      {/* Filters */}
      <FilterBar>
        <SearchBox>
          <FiSearch />
          <input
            type="text"
            placeholder="Search by code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBox>
        <StatusFilter>
          <FiFilter />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="scheduled">Scheduled</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="expired">Expired</option>
            <option value="archived">Archived</option>
          </select>
        </StatusFilter>
      </FilterBar>

      {/* Coupons Table */}
      {loading ? (
        <LoadingState><FiLoader /> Loading coupons...</LoadingState>
      ) : error ? (
        <ErrorState><FiAlertCircle /> {error}</ErrorState>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Discount</th>
                <th>Status</th>
                <th>Usage</th>
                <th>Dates</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCoupons.map((coupon) => (
                <tr key={coupon.id}>
                  <td>
                    <CodeBadge>{coupon.code}</CodeBadge>
                  </td>
                  <td>{coupon.name || '-'}</td>
                  <td>
                    <DiscountBadge type={coupon.discount_type}>
                      {coupon.discount_type === 'fixed' ? <FiDollarSign /> : <FiPercent />}
                      {coupon.discount_value}
                      {coupon.discount_type === 'percentage' && '%'}
                    </DiscountBadge>
                  </td>
                  <td>
                    <StatusBadge status={coupon.status}>
                      {getStatusIcon(coupon.status)}
                      {coupon.status}
                    </StatusBadge>
                  </td>
                  <td>
                    {coupon.current_uses}
                    {coupon.max_total_uses ? ` / ${coupon.max_total_uses}` : ' / ∞'}
                  </td>
                  <td>
                    <DateInfo>
                      {new Date(coupon.start_date).toLocaleDateString()} -<br />
                      {new Date(coupon.end_date).toLocaleDateString()}
                    </DateInfo>
                  </td>
                  <td>
                    <ActionButtons>
                      <IconButton
                        onClick={() => {
                          setSelectedCoupon(coupon);
                          setShowDetailModal(true);
                        }}
                        title="View Details"
                      >
                        <FiEye />
                      </IconButton>

                      {coupon.status === 'draft' && (
                        <IconButton
                          onClick={() => handleLifecycleAction(coupon.id, 'activate')}
                          color="#10b981"
                          title="Activate"
                        >
                          <FiCheck />
                        </IconButton>
                      )}

                      {coupon.status === 'active' && (
                        <IconButton
                          onClick={() => handleLifecycleAction(coupon.id, 'pause', 'Manually paused')}
                          color="#8b5cf6"
                          title="Pause"
                        >
                          <FiPause />
                        </IconButton>
                      )}

                      {coupon.status === 'paused' && (
                        <IconButton
                          onClick={() => handleLifecycleAction(coupon.id, 'resume')}
                          color="#10b981"
                          title="Resume"
                        >
                          <FiPlay />
                        </IconButton>
                      )}

                      {(coupon.status === 'draft' || coupon.status === 'expired') && coupon.current_uses === 0 && (
                        <IconButton
                          onClick={() => handleDelete(coupon.id)}
                          color="#ef4444"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </IconButton>
                      )}

                      {!['archived'].includes(coupon.status) && (
                        <IconButton
                          onClick={() => handleLifecycleAction(coupon.id, 'archive', 'Manually archived')}
                          color="#374151"
                          title="Archive"
                        >
                          <FiArchive />
                        </IconButton>
                      )}
                    </ActionButtons>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {/* Create Coupon Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <ModalOverlay onClick={() => setShowCreateModal(false)}>
            <ModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <ModalHeader>
                <h2><FiPlus /> Create New Coupon</h2>
                <CloseButton onClick={() => setShowCreateModal(false)}><FiX /></CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <FormGrid>
                  <FormGroup>
                    <label>Coupon Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="SUMMER2025"
                      maxLength={50}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Summer Sale 2025"
                    />
                  </FormGroup>

                  <FormGroup fullWidth>
                    <label>Description</label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Special summer discount for all courses"
                      rows={3}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Discount Type *</label>
                    <select
                      value={formData.discount_type}
                      onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as any })}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </FormGroup>

                  <FormGroup>
                    <label>Discount Value *</label>
                    <input
                      type="number"
                      value={formData.discount_value}
                      onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                      placeholder={formData.discount_type === 'percentage' ? '20' : '50'}
                      min="0"
                      step={formData.discount_type === 'percentage' ? '1' : '0.01'}
                      max={formData.discount_type === 'percentage' ? '100' : undefined}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Min Purchase Amount</label>
                    <input
                      type="number"
                      value={formData.min_purchase_amount}
                      onChange={(e) => setFormData({ ...formData, min_purchase_amount: e.target.value })}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </FormGroup>

                  {formData.discount_type === 'percentage' && (
                    <FormGroup>
                      <label>Max Discount Cap</label>
                      <input
                        type="number"
                        value={formData.max_discount_amount}
                        onChange={(e) => setFormData({ ...formData, max_discount_amount: e.target.value })}
                        placeholder="100.00"
                        min="0"
                        step="0.01"
                      />
                    </FormGroup>
                  )}

                  <FormGroup>
                    <label>Start Date *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>End Date *</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Max Total Uses</label>
                    <input
                      type="number"
                      value={formData.max_total_uses}
                      onChange={(e) => setFormData({ ...formData, max_total_uses: e.target.value })}
                      placeholder="Unlimited"
                      min="1"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Max Uses Per User *</label>
                    <input
                      type="number"
                      value={formData.max_uses_per_user}
                      onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                      min="1"
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Applicable To</label>
                    <select
                      value={formData.applicable_to}
                      onChange={(e) => setFormData({ ...formData, applicable_to: e.target.value as any })}
                    >
                      <option value="all">All Users</option>
                      <option value="new_users">New Users Only</option>
                      <option value="existing_users">Existing Users</option>
                    </select>
                  </FormGroup>

                  <FormGroup fullWidth>
                    <label>Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Internal notes (optional)"
                      rows={2}
                    />
                  </FormGroup>
                </FormGrid>
              </ModalBody>

              <ModalFooter>
                <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleCreate}
                  disabled={!formData.code || !formData.discount_value || !formData.start_date || !formData.end_date}
                >
                  <FiCheck /> Create Coupon
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>

      {/* Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedCoupon && (
          <ModalOverlay onClick={() => setShowDetailModal(false)}>
            <ModalContent
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <ModalHeader>
                <h2><FiEye /> Coupon Details</h2>
                <CloseButton onClick={() => setShowDetailModal(false)}><FiX /></CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <DetailGrid>
                  <DetailItem>
                    <DetailLabel>Code</DetailLabel>
                    <CodeBadge large>{selectedCoupon.code}</CodeBadge>
                  </DetailItem>
                  
                  <DetailItem>
                    <DetailLabel>Status</DetailLabel>
                    <StatusBadge status={selectedCoupon.status} large>
                      {getStatusIcon(selectedCoupon.status)}
                      {selectedCoupon.status}
                    </StatusBadge>
                  </DetailItem>

                  <DetailItem fullWidth>
                    <DetailLabel>Name</DetailLabel>
                    <DetailValue>{selectedCoupon.name || '-'}</DetailValue>
                  </DetailItem>

                  <DetailItem fullWidth>
                    <DetailLabel>Description</DetailLabel>
                    <DetailValue>{selectedCoupon.description || '-'}</DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Discount</DetailLabel>
                    <DetailValue>
                      {selectedCoupon.discount_type === 'fixed' ? '$' : ''}
                      {selectedCoupon.discount_value}
                      {selectedCoupon.discount_type === 'percentage' ? '%' : ''}
                    </DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Min Purchase</DetailLabel>
                    <DetailValue>
                      {selectedCoupon.min_purchase_amount ? `$${selectedCoupon.min_purchase_amount}` : 'None'}
                    </DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Usage</DetailLabel>
                    <DetailValue>
                      {selectedCoupon.current_uses} / {selectedCoupon.max_total_uses || '∞'}
                    </DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Per User Limit</DetailLabel>
                    <DetailValue>{selectedCoupon.max_uses_per_user}</DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Start Date</DetailLabel>
                    <DetailValue>{new Date(selectedCoupon.start_date).toLocaleString()}</DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>End Date</DetailLabel>
                    <DetailValue>{new Date(selectedCoupon.end_date).toLocaleString()}</DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Applicable To</DetailLabel>
                    <DetailValue>{selectedCoupon.applicable_to.replace('_', ' ')}</DetailValue>
                  </DetailItem>

                  <DetailItem>
                    <DetailLabel>Created</DetailLabel>
                    <DetailValue>{new Date(selectedCoupon.created_at).toLocaleString()}</DetailValue>
                  </DetailItem>
                </DetailGrid>
              </ModalBody>

              <ModalFooter>
                <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default CouponManagement;

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #3b82f6;
  }
`;

const ActionButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div<{ color?: string }>`
  background: white;
  padding: 20px;
  border-radius: 12px;
  border-left: 4px solid ${props => props.color || '#3b82f6'};
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 8px;
`;

const StatValue = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const SearchBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 16px;
  gap: 12px;

  svg {
    color: #9ca3af;
  }

  input {
    flex: 1;
    border: none;
    padding: 12px 0;
    font-size: 14px;
    outline: none;
  }
`;

const StatusFilter = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0 16px;
  gap: 12px;

  svg {
    color: #9ca3af;
  }

  select {
    border: none;
    padding: 12px 0;
    font-size: 14px;
    outline: none;
    background: transparent;
    cursor: pointer;
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  thead {
    background: #f9fafb;
    
    th {
      padding: 16px;
      text-align: left;
      font-weight: 600;
      color: #374151;
      font-size: 14px;
      border-bottom: 2px solid #e5e7eb;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e5e7eb;
      transition: background 0.2s;

      &:hover {
        background: #f9fafb;
      }

      td {
        padding: 16px;
        font-size: 14px;
        color: #4b5563;
      }
    }
  }
`;

const CodeBadge = styled.span<{ large?: boolean }>`
  display: inline-block;
  background: #dbeafe;
  color: #1e40af;
  padding: ${props => props.large ? '8px 16px' : '4px 12px'};
  border-radius: 6px;
  font-weight: 600;
  font-size: ${props => props.large ? '16px' : '13px'};
  font-family: 'Courier New', monospace;
`;

const DiscountBadge = styled.span<{ type: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.type === 'fixed' ? '#d1fae5' : '#fef3c7'};
  color: ${props => props.type === 'fixed' ? '#065f46' : '#92400e'};
  padding: 4px 12px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 13px;
`;

const StatusBadge = styled.span<{ status: string; large?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${props => {
    const colors: {[key: string]: string} = {
      draft: '#f3f4f6',
      scheduled: '#fef3c7',
      active: '#d1fae5',
      paused: '#e9d5ff',
      expired: '#fee2e2',
      archived: '#f3f4f6'
    };
    return colors[props.status] || '#f3f4f6';
  }};
  color: ${props => {
    const colors: {[key: string]: string} = {
      draft: '#374151',
      scheduled: '#92400e',
      active: '#065f46',
      paused: '#5b21b6',
      expired: '#991b1b',
      archived: '#111827'
    };
    return colors[props.status] || '#374151';
  }};
  padding: ${props => props.large ? '8px 16px' : '4px 12px'};
  border-radius: 6px;
  font-weight: 600;
  font-size: ${props => props.large ? '14px' : '12px'};
  text-transform: capitalize;
`;

const DateInfo = styled.div`
  font-size: 12px;
  line-height: 1.4;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const IconButton = styled.button<{ color?: string }>`
  background: ${props => props.color || '#3b82f6'};
  color: white;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #6b7280;
  font-size: 16px;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #ef4444;
  font-size: 16px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled(motion.div)`
  background: white;
  border-radius: 16px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
`;

const ModalHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;

  h2 {
    font-size: 24px;
    font-weight: 700;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 12px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  overflow-y: auto;
  flex: 1;
`;

const ModalFooter = styled.div`
  padding: 24px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const FormGroup = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-weight: 600;
    color: #374151;
    font-size: 14px;
  }

  input, select, textarea {
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #3b82f6;
    }
  }

  textarea {
    resize: vertical;
    font-family: inherit;
  }
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: #3b82f6;
    color: white;

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background: #f3f4f6;
    color: #374151;

    &:hover {
      background: #e5e7eb;
    }
  `}
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
`;

const DetailItem = styled.div<{ fullWidth?: boolean }>`
  grid-column: ${props => props.fullWidth ? 'span 2' : 'span 1'};
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

const DetailValue = styled.div`
  font-size: 16px;
  color: #1f2937;
  font-weight: 500;
`;

